import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Download, FileText, Info, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { invoicesApi, type Invoice, type InvoiceAppointment, type InvoiceStatus } from '../../api/invoices'
import { sourceLabel } from '../../lib/leadSource'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { PageSpinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Modal } from '../../components/ui/Modal'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { useAuthStore } from '../../store/auth'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'created', label: 'Created' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'paid', label: 'Paid' },
]

// Draft and created proformas are still editable, so they need no copy.
const COPYABLE: InvoiceStatus[] = ['sent', 'accepted', 'declined', 'withdrawn', 'paid']

const SHARED_IN_LABELS: Record<string, string> = {
  email: 'Email', whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook',
}

const dateTime = (iso: string) => {
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

/**
 * Appointment cell: the booked date, plus an info icon whose hover card carries
 * the rest. The card is `position: fixed` and anchored to the icon — an absolute
 * one would be clipped by the table's own scroll container on the lower rows.
 */
function AppointmentCell({ appointment: a }: { appointment: InvoiceAppointment }) {
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)

  const show = (e: React.MouseEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    setAnchor({ x: r.right, y: r.bottom + 6 })
  }

  const endTime = a.endAt
    ? new Date(a.endAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-gray-700">{a.startAt ? dateTime(a.startAt) : '—'}</span>
      <span
        onMouseEnter={show}
        onMouseLeave={() => setAnchor(null)}
        className="inline-flex text-gray-400 hover:text-primary-600 cursor-help"
        aria-label="Appointment details"
      >
        <Info className="w-3.5 h-3.5" />
      </span>
      {anchor && (
        <div
          style={{
            position: 'fixed',
            top: Math.min(anchor.y, window.innerHeight - 210),
            left: Math.max(8, anchor.x - 272),
            zIndex: 200,
            width: 272,
          }}
          className="rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-xl pointer-events-none"
        >
          <dl className="space-y-1.5">
            <div>
              <dt className="text-gray-400">Appointment</dt>
              <dd className="font-medium text-gray-900">
                {a.startAt ? dateTime(a.startAt) : '—'}{endTime ? ` – ${endTime}` : ''}
              </dd>
            </div>
            {a.leadName && (
              <div><dt className="text-gray-400">Lead</dt><dd className="text-gray-800">{a.leadName}</dd></div>
            )}
            {a.leadSource && (
              <div><dt className="text-gray-400">Source</dt><dd className="text-gray-800">{sourceLabel(a.leadSource)}</dd></div>
            )}
            {a.note && (
              <div>
                <dt className="text-gray-400">Note</dt>
                <dd className="whitespace-pre-wrap break-words text-gray-800">{a.note}</dd>
              </div>
            )}
            <div className="border-t border-gray-100 pt-1.5 text-gray-400">
              Created {dateTime(a.createdAt)}
              {a.createdByUser && ` by ${a.createdByUser.firstName} ${a.createdByUser.lastName}`}
            </div>
          </dl>
        </div>
      )}
    </span>
  )
}

export function InvoicesList() {
  const { companyId = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  // Mirrors InvoicesService.remove: sales users may delete only their own drafts.
  const canDelete = (inv: Invoice) =>
    user?.role !== 'sales_user' || (inv.status === 'draft' && inv.createdById === user.id)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  // An accepted proforma's copy is confirmed first: it carries no appointment.
  const [copySource, setCopySource] = useState<Invoice | null>(null)

  const params = {
    status: (status || undefined) as InvoiceStatus | undefined,
    search: search || undefined, page, limit: 25,
  }
  const { data, isLoading } = useQuery({
    queryKey: ['invoices', companyId, params],
    queryFn: () => invoicesApi.list(companyId, params),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => invoicesApi.remove(companyId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices', companyId] }); setDeleteId(null) },
  })

  const copyMut = useMutation({
    mutationFn: (id: string) => invoicesApi.copy(companyId, id),
    onSuccess: inv => {
      qc.invalidateQueries({ queryKey: ['invoices', companyId] })
      setCopySource(null)
      toast.success(`Copied as ${inv.number}`)
      navigate(`/company/${companyId}/invoices/${inv.id}/edit`)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg ?? 'Could not copy the proforma'))
    },
  })

  const requestCopy = (inv: Invoice) => {
    if (inv.status === 'accepted') setCopySource(inv)
    else copyMut.mutate(inv.id)
  }

  const download = async (inv: Invoice) => {
    const blob = await invoicesApi.pdfBlob(companyId, inv.id)
    const url = URL.createObjectURL(blob)
    Object.assign(document.createElement('a'), { href: url, download: `${inv.number}.pdf` }).click()
    URL.revokeObjectURL(url)
  }

  const toDelete = data?.data.find(i => i.id === deleteId)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <h1 className="page-title">Proformas</h1>
          {data && <span className="badge badge-gray">{data.meta.total}</span>}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/company/${companyId}/invoices/new`)}>
          <Plus className="w-4 h-4" /> New proforma
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 px-6 py-3 border-b border-gray-100 bg-white">
        <input className="input max-w-xs" placeholder="Search number or lead name…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
        <DropdownSelect value={status} options={STATUS_OPTIONS} onChange={v => { setStatus(v); setPage(1) }} />
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto bg-white">
        {isLoading ? <PageSpinner /> : (
          <table className="table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Lead name</th>
                <th>Linked to</th>
                <th>Total</th>
                <th>Status</th>
                <th>Appointment</th>
                <th>Shared via</th>
                <th>Created by</th>
                <th>Created</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-16">
                    <EmptyState
                      icon={<FileText className="w-10 h-10" />}
                      title="No proformas yet"
                      description="Create your first proforma"
                      action={<button onClick={() => navigate(`/company/${companyId}/invoices/new`)} className="btn btn-primary btn-sm">New Proforma</button>}
                    />
                  </td>
                </tr>
              )}
              {(data?.data ?? []).map((inv: Invoice) => (
                <tr key={inv.id}>
                  <td>
                    <Link
                      className="text-primary-600 hover:text-primary-700 font-medium font-mono"
                      to={`/company/${companyId}/invoices/${inv.id}/edit`}
                    >
                      {inv.number}
                    </Link>
                  </td>
                  <td className="font-medium">{inv.patientName}</td>
                  <td>
                    {inv.leadId && <Link className="text-blue-600 hover:underline text-xs" to={`/company/${companyId}/leads/${inv.leadId}`}>Lead ↗</Link>}
                    {inv.dealId && <Link className="text-blue-600 hover:underline text-xs" to={`/company/${companyId}/deals/${inv.dealId}`}>Deal ↗</Link>}
                    {!inv.leadId && !inv.dealId && <span className="text-gray-400">—</span>}
                  </td>
                  <td>{Number(inv.total).toFixed(2)} {inv.currency}</td>
                  <td>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td>
                    {inv.appointment
                      ? <AppointmentCell appointment={inv.appointment} />
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td>
                    {inv.sharedIn
                      ? <span className="badge badge-gray">{SHARED_IN_LABELS[inv.sharedIn] ?? inv.sharedIn}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-gray-600">
                    {inv.createdByUser ? `${inv.createdByUser.firstName} ${inv.createdByUser.lastName}` : '—'}
                  </td>
                  <td className="text-gray-500">{new Date(inv.createdAt).toLocaleDateString('en-GB')} {new Date(inv.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="text-gray-500">{new Date(inv.updatedAt).toLocaleDateString('en-GB')} {new Date(inv.updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="whitespace-nowrap">
                    <button className="btn btn-ghost btn-sm" title="Download PDF" onClick={() => download(inv)}>
                      <Download className="w-4 h-4" />
                    </button>
                    {COPYABLE.includes(inv.status) && (
                      <button className="btn btn-ghost btn-sm" title="Copy to a new draft"
                        disabled={copyMut.isPending} onClick={() => requestCopy(inv)}>
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete(inv) && (
                      <button className="btn btn-ghost btn-sm text-red-600" title="Delete" onClick={() => setDeleteId(inv.id)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {(data?.meta?.totalPages ?? 0) > 1 && (
        <div className="flex items-center gap-3 px-6 py-3 border-t border-gray-100 text-sm">
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {data!.meta.page} / {data!.meta.totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= data!.meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      <Modal open={!!copySource} onClose={() => setCopySource(null)} title="Copy accepted proforma" size="sm">
        <div className="py-1">
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-2">
            {copySource?.appointment?.startAt ? (
              <p>
                {copySource.number} has an appointment on <strong>{dateTime(copySource.appointment.startAt)}</strong>.
              </p>
            ) : (
              <p>{copySource?.number} is accepted.</p>
            )}
            <p>
              The copy starts as a draft without an appointment. When you accept the copy you'll book the
              appointment again — keep this date and time or choose a new one.
            </p>
            <p>Withdrawing {copySource?.number} afterwards cancels its upcoming appointment.</p>
          </div>
          <div className="flex gap-2 pt-4">
            <button className="btn btn-secondary btn-sm flex-1" onClick={() => setCopySource(null)}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm flex-1" disabled={copyMut.isPending}
              onClick={() => copySource && copyMut.mutate(copySource.id)}>
              {copyMut.isPending ? 'Copying…' : 'Copy'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        title="Delete proforma"
        message={`Delete ${toDelete?.number ?? ''}? This cannot be undone.`}
        confirmLabel="Delete"
        isPending={deleteMut.isPending}
      />
    </div>
  )
}
